import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import TutorialModal from "../components/ui/TutorialModal";
import { useTranslation } from "react-i18next";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const { t } = useTranslation();

  const tutorialSteps = [
    {
      title: t("tutorials.provider.title"),
      content: t("tutorials.provider.content"),
      image: "",
      tips: t("tutorials.provider.tips", { returnObjects: true }),
    },
  ];

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        fetchUserRoles(user);
        await loadUserPermissions(user.id);
      } else {
        setCurrentUser(null);
        setRoles([]);
        setLoading(false);
      }
    };

    const { subscription } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          fetchUserRoles(session.user);
        } else {
          setCurrentUser(null);
          setRoles([]);
          setPermissions([]);
          setLoading(false);
        }
      }
    );
    fetchUser();
    return () => subscription?.unsubscribe && subscription.unsubscribe();
  }, []);

  const fetchUserRoles = async (user) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("roles(name), is_new")
        .eq("user_id", user.id);

      if (error) {
        console.error("Erreur lors de la récupération des rôles :", error);
        setRoles([]);
      } else if (data) {
        const userRoles = data.map((role) => role.roles.name);
        setRoles(userRoles || []);

        const newProviderEntry = data.find(
          (role) => role.roles.name === "provider" && role.is_new === true
        );

        if (newProviderEntry) {
          setShowTutorial(true);
          await supabase
            .from("user_roles")
            .update({ is_new: false })
            .eq("user_id", user.id)
            .eq("role_id", 2); // 2 = id du rôle "provider"
        }
      } else {
        console.error("Utilisateur introuvable dans la base de données.");
        setRoles([]);
      }

      setCurrentUser(user);
    } catch (error) {
      console.error("Erreur lors de la récupération des rôles :", error);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/permissions/me`,
        {
          credentials: "include",
          headers: {
            "X-User-Id": userId,
          },
        }
      );

      if (!res.ok) {
        console.error("Impossible de charger les permissions utilisateur.");
        setPermissions([]);
        return;
      }

      const data = await res.json();
      setPermissions(data || []);
    } catch (err) {
      console.error("Erreur de chargement des permissions :", err);
      setPermissions([]);
    }
  };

  const hasPermission = (code) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.some((p) => p.code === code);
  };

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signup = async (email, password, displayName) => {
    try {
      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { display_name: displayName },
          },
        });

      if (signUpError) {
        throw signUpError;
      }

      setCurrentUser(signUpData.user);
    } catch (error) {
      console.error("Erreur lors de la création du compte :", error.message);
      throw error;
    }
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        roles,
        login,
        signup,
        logout,
        permissions,
        hasPermission,
      }}
      d
    >
      {!loading && children}
      <TutorialModal
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        steps={tutorialSteps}
      />
    </AuthContext.Provider>
  );
}
